import { useCallback, useEffect, useRef, useState } from 'react';

import { snapshotValue } from './loanSectionForm';
import { useLoanSectionDirty } from './LoanSectionFormContext';

type Options<T> = {
  sectionKey: string;
  applicationId: string;
  applicationUpdatedAt?: string;
  initialValue: T;
  readOnly?: boolean;
};

export function useLoanSectionForm<T>({
  sectionKey,
  applicationId,
  applicationUpdatedAt,
  initialValue,
  readOnly,
}: Options<T>) {
  const savedSnapshot = useRef(snapshotValue(initialValue));
  const [draft, setDraft] = useState<T>(initialValue);

  const syncFromServer = useCallback((value: T) => {
    savedSnapshot.current = snapshotValue(value);
    setDraft(value);
  }, []);

  const serverSnapshot = snapshotValue(initialValue);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    const incoming = snapshotValue(initialValue);
    if (incoming === savedSnapshot.current) return;
    // Don't clobber in-progress edits when a background refetch arrives.
    if (snapshotValue(draftRef.current) !== savedSnapshot.current) return;
    syncFromServer(initialValue);
  }, [
    applicationId,
    applicationUpdatedAt,
    serverSnapshot,
    syncFromServer,
    initialValue,
  ]);

  const isDirty = snapshotValue(draft) !== savedSnapshot.current;

  useLoanSectionDirty(sectionKey, isDirty && !readOnly);

  const markSaved = useCallback(
    (value?: T) => {
      const next = value ?? draft;
      savedSnapshot.current = snapshotValue(next);
      setDraft(next);
    },
    [draft]
  );

  const resetDraft = useCallback(() => {
    setDraft(JSON.parse(savedSnapshot.current) as T);
  }, []);

  /** Apply server/listing sync without marking the section unsaved. */
  const syncDraftToSaved = useCallback((value: T) => {
    savedSnapshot.current = snapshotValue(value);
    setDraft(value);
  }, []);

  const mergeDraftFromListing = useCallback((merger: (prev: T) => T) => {
    setDraft((prev) => {
      const next = merger(prev);
      savedSnapshot.current = snapshotValue(next);
      return next;
    });
  }, []);

  return {
    draft,
    setDraft,
    isDirty,
    markSaved,
    resetDraft,
    syncDraftToSaved,
    mergeDraftFromListing,
    readOnly: Boolean(readOnly),
  };
}
